import Classification from 'models/Classification';
import Group, { GroupOptions } from 'models/Group';
import Patient, { PatientOptions } from 'models/Patient';

export type PendingCoordinateRequestOptions = {
  id: string;
  status: string;
  data: {
    importedPatientId?: string;
    providerGroupId?: string;
    admissionDate?: string;
  };
  providerGroup?: Partial<GroupOptions> | null;
};

interface ImportedPatientOptions extends PatientOptions {
  episodeClassification?: Classification;
  pendingCoordinateRequest?: PendingCoordinateRequestOptions | null;
}

export default class ImportedPatient extends Patient {
  pendingCoordinateRequest: PendingCoordinateRequestOptions | null;
  pendingCoordinateRequestProviderGroup: Group | null;

  constructor(opts: Partial<ImportedPatientOptions> = {}) {
    // Currently don't support multiple episode classifications for imported patients, so
    // convert the incoming single value to an array for the Patient model used on the Coordinate form,
    // at which point multiple can be selected.
    super({ ...opts, episodeClassifications: opts.episodeClassification ? [opts.episodeClassification] : [] });

    this.pendingCoordinateRequest = opts.pendingCoordinateRequest ?? null;
    this.pendingCoordinateRequestProviderGroup = opts.pendingCoordinateRequest?.providerGroup
      ? new Group(opts.pendingCoordinateRequest.providerGroup)
      : null;
  }

  intakeFormValues() {
    const baseValues = super.intakeFormValues();
    const review = this.pendingCoordinateRequest;
    if (!review) return baseValues;

    const providerGroup = this.pendingCoordinateRequestProviderGroup;

    // The review's admissionDate is the provider's *actual* admission date, which is not the
    // same thing as the patient's hospital admission date — don't let it overwrite admittedOn.
    // The API reads that date off the pending request and admits the patient on completion.
    return {
      ...baseValues,
      group: providerGroup ?? baseValues.group,
      locationType: providerGroup
        ? {
            label: providerGroup.groupType?.displayName ?? '',
            value: providerGroup.groupType?.apiName ?? '',
          }
        : baseValues.locationType,
    };
  }
}
